import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

interface DocumentSearchParams {
  pageId: string | null;
  reportId: string | null;
  tab: "report" | "snapshot" | "chat" | "notes" | "chronology" | "summary";
  sectionId: string | null;
}

type UpdateParams = Partial<DocumentSearchParams>;

type UseDocumentSearchParamsReturn = [
  currentParams: DocumentSearchParams,
  updateParams: (params: UpdateParams) => void,
];

export const useDocumentSearchParams = (): UseDocumentSearchParamsReturn => {
  const [searchParams, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);

  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  }, [setSearchParams]);

  const currentParams: DocumentSearchParams = {
    tab: searchParams.get("tab") as
      | "report"
      | "snapshot"
      | "chat"
      | "notes"
      | "chronology"
      | "summary",
    pageId: searchParams.get("pageId"),
    reportId: searchParams.get("reportId"),
    sectionId: searchParams.get("sectionId"),
  };

  const updateParams = useCallback((params: UpdateParams) => {
    setSearchParamsRef.current((prev) => {
      const newSearchParams = new URLSearchParams(prev);

      Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined) {
          newSearchParams.delete(key);
        } else if (typeof value === "string" || typeof value === "number") {
          newSearchParams.set(key, value.toString());
        }
      });

      return newSearchParams;
    });
  }, []);

  return [currentParams, updateParams];
};
